import React, {
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  Box,
  Grid,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material'
import CustomBreadcrumbs from '../../components/BreadcrumbsComponent/CustomBreadcrumbs'
import { PAGE_SIZE } from '../../services/common/const';
import {
  Link,
  useLocation,
  useNavigate
} from 'react-router-dom';
import SearchIcon from "@mui/icons-material/Search";
import SkeletonWrapper from '../../components/Skeleton/SkeletonWrapper';
import { EncryptDecryptService } from '../../services/common/EncryptionService';
import { PreferencesContext } from '../../PreferenceContext';
import dayjs from 'dayjs';
import { GetReferBookEntryList } from '../../services/ReferBookEntryListService';

// SCHEMA
interface Data {
  notificationId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  notificationId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  notificationId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
};

type Order = "desc" | "asc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// SCHEMA FOR RESULTDATA
interface ResultData {
  referBookEntryColl: Array<any>;
  referReasonColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  referBookEntryColl: [],
  referReasonColl: [],
  tabTypeColl: [],
  totalCount: 0,
};

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

const ReferBookEntryList = () => {

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  };

  const queryParams = useQuery();
  const pageFromUrl = parseInt(queryParams.get("page") || "1", 10);
  const encodedQuery = queryParams.get("q") || "";
  const decodedQuery = decodeURIComponent(encodedQuery);

  const [param, setParam] = useState({
    ...parameters,
    pageIndex: pageFromUrl,
    searchValue: decodedQuery,
    startDate: queryParams.get("fd") || "",
    endDate: queryParams.get("ed") || "",
  });

  const [searchValue, setSearchValue] = useState(param.searchValue);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [order, setOrder] = React.useState<Order>("");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [page_Index, setPage_Index] = useState(1);
  const [counData, setCounData] = useState(0);
  const [Curr_page, setCurrPage] = React.useState(1);

  const debouncedSearch = useMemo(() => {
    let handler: NodeJS.Timeout;
    return (value: string) => {
      clearTimeout(handler);
      handler = setTimeout(() => {
        const val = encodeURIComponent(value.trim());

        // 🔧 Fix: Update param state before navigation
        setParam(prev => ({
          ...prev,
          searchValue: decodeURIComponent(val),
          pageIndex: 1
        }));

        navigate(`?page=1&q=${val}&fd=${param.startDate}&ed=${param.endDate}`);
      }, 400);
    };
  }, [navigate, param.startDate, param.endDate]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.referBookEntryColl?.map(
        (n: any) => n?.referBookEntryId || 0
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "20%"
            }}
            align="left"
          >
            Ref by Doctor (Qualification)
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "12%"
            }}
            align="left"
          >
            Mobile Number
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "12%",
              display: {
                xs: "none",
                sm: "table-cell"
              }
            }}
            align="left"
          >
            Ref Date
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "18%",
              display: {
                xs: "none",
                sm: "table-cell"
              }
            }}
            align="left"
          >
            Location
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "18%"
            }}
            align="left"
          >
            Patient Name
          </TableCell>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "20%"
            }}
            align="left"
          >
            Reason For Reference
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number]
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (Curr_page !== value) {
      setCurrPage(value);
      setPage_Index(value);
      setParam({ ...param, pageIndex: value });
    }
  };

  const getReferBookEntryList = async () => {
    try {
      let params = new URLSearchParams();

      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetReferBookEntryList(params);
      if (result?.data?.data) {
        const resultData: ResultData = result?.data?.data;
        let referBookEntryColl = resultData?.referBookEntryColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (page_Index - 1) * param.pageSize + 1;
        let endIndex =
          (page_Index - 1) * param.pageSize + (referBookEntryColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  // Reason For Reference Name
  const reasonForReference = useMemo(() => {
    const map: any = {};
    resultData?.referReasonColl?.forEach((item: any) => {
      if (!map[item.referBookEntryId]) {
        map[item.referBookEntryId] = [];
      }
      map[item.referBookEntryId].push(item.reasonForReferenceLabel);
    });
    return map;
  }, [resultData]);

  const fetchData = async () => {
    try {
      await getReferBookEntryList();
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
    setIsLoading(false);
  }, [param]);

  return (
    <Box>
      <Box className="main-container">
        <Grid container className="page-header">
          <Grid item xs={6}>
            <Box sx={{ display: "flex", height: "100%", alignItems: "end" }}>
              <CustomBreadcrumbs />
            </Box>
          </Grid>

          <Grid container className="list-container" mt={2}>
            <Grid item xs={12}>
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} sm={6} md={3} xl={2}>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-search-box"
                  >
                    <TextField
                      id="searchBox"
                      placeholder="Search..."
                      value={searchValue}
                      defaultValue={decodedQuery}
                      onChange={handleSearchChange}
                      size="small"
                      fullWidth
                      sx={{ flexShrink: 0 }}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                      }}
                    />
                  </SkeletonWrapper>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} mt={2}>
              <TableContainer>
                <Table>
                  <EnhancedTableHead
                    numSelected={resultData?.referBookEntryColl?.length || 0}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleCheckSelectAll}
                    rowCount={resultData?.referBookEntryColl?.length}
                  />
                  <TableBody>
                    {isLoading ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input">
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.referBookEntryColl,
                          getComparator(order, orderBy)
                        )
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((item: any, index: any) => {
                            return (
                              <TableRow
                                key={`referBookEntry-row-${item?.referBookEntryId || index}`}>
                                <TableCell>
                                  <Link
                                    key={`link-${item?.referBookEntryId}`}
                                    className="text-link"
                                    to={{
                                      pathname: "refer-book-entry-details",
                                      search: `?q=${encodeURIComponent(
                                        edService.encrypt(
                                          item?.referBookEntryId || 0
                                        ) || 0
                                      )}`,
                                    }}>
                                    {item?.fromDoctorName}
                                    {item?.fromDoctorQualification && (
                                      <>
                                        <br />
                                        ({item.fromDoctorQualification})
                                      </>
                                    )}
                                  </Link>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  {item?.fromDoctorContactNumber}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  {item?.referDate
                                    ? dayjs(item.referDate).format("DD/MM/YYYY hh:mm A")
                                    : ""}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  {item?.toLocationName}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  <div>{item?.forPatientFirstName} {item?.forPatientLastName}</div>
                                  <div>
                                    {item?.forPatientGender || null}
                                    {(item?.forPatientAgeYear > 0 ||
                                      item?.forPatientAgeMonth > 0 ||
                                      item?.forPatientAgeDay > 0) ? ' / ' : ''}

                                    {item?.forPatientAgeYear > 0
                                      ? `${item.forPatientAgeYear}y `
                                      : ''}
                                    {item?.forPatientAgeMonth > 0
                                      ? `${item.forPatientAgeMonth}m `
                                      : ''}
                                    {item?.forPatientAgeDay > 0
                                      ? `${item.forPatientAgeDay}d`
                                      : ''}
                                  </div>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  {reasonForReference[item?.referBookEntryId]?.join(", ") || ""}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {resultData?.referBookEntryColl?.length <= 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} paddingTop={1}>
              {resultData?.referBookEntryColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-label"
                    >
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-stack"
                    >
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={Curr_page} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default ReferBookEntryList
